import { ChangeDetectionStrategy, Component, computed, EventEmitter, Output } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { RestTimerService } from 'services/rest-timer.service';
import {
  formatRestDuration,
  REST_TIMER_MAX_SECONDS,
  REST_TIMER_MIN_SECONDS,
  REST_TIMER_STEP_SECONDS,
} from '@cf-shared/data-model/rest-timer';

@Component({
  selector: 'cs-rest-timer-expanded',
  templateUrl: 'cs-rest-timer-expanded.html',
  styleUrls: ['cs-rest-timer-expanded.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CsRestTimerExpandedComponent {
  @Output() collapse = new EventEmitter<void>();
  protected readonly LL = LL;

  readonly isRunning = this.restTimer.isRunning;
  readonly exerciseName = this.restTimer.exerciseName;
  readonly remainingSeconds = this.restTimer.remainingSeconds;
  readonly isExpired = this.restTimer.isExpired;
  readonly timeLabel = computed(() => {
    const s = this.remainingSeconds();
    return s == null ? '' : formatRestDuration(s);
  });
  // if either boundary is reached OR the timer has already expired, disable
  // the step buttons (nothing useful to do after "Done!")
  readonly canDecrement = computed(() =>
    !this.isExpired() && (this.remainingSeconds() ?? 0) > REST_TIMER_MIN_SECONDS
  );
  readonly canIncrement = computed(() =>
    !this.isExpired() && (this.remainingSeconds() ?? 0) < REST_TIMER_MAX_SECONDS
  );
  // platform gate: native, or web with the test-mode debug flag set
  get isAllowed(): boolean {
    return this.restTimer.isAllowedOnThisPlatform();
  }

  constructor(private readonly restTimer: RestTimerService) {}

  // user tapped -15s: shrink the remaining time by one step
  async onDecrement(): Promise<void> {
    await this.restTimer.adjust(-REST_TIMER_STEP_SECONDS);
  }

  // user tapped +15s: extend the remaining time by one step
  async onIncrement(): Promise<void> {
    await this.restTimer.adjust(REST_TIMER_STEP_SECONDS);
  }

  // user tapped Skip: cancel the running timer and collapse back to no overlay
  async onSkip(): Promise<void> {
    await this.restTimer.skip();
    this.collapse.emit();
  }

  // user tapped outside the panel: collapse back to the pill without cancelling
  onBackdrop(): void {
    this.collapse.emit();
  }
}
